import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import GlobalStyles from './styles/GlobalStyles';
import { AppProvider } from './context/AppContext';
import { applyPolyfills } from './utils/polyfills';
import { setupGlobalErrorHandlers } from './utils/errorHandlers';
import { startChatHealthObservers } from './utils/chatHealthCounters';
import { installPageLeavingWatch, logFetchFailure } from './utils/fetchFailureLog';
import { installMarkSpinnerPhase } from './utils/markSpinnerPhase';
import ErrorBoundary from './components/ErrorBoundary';
import SiteDefaultHead from './components/SiteDefaultHead';
import { captureShellStructuredData } from './config/shellStructuredData';
import app from './utils/firebase';
// Firestore MUST be configured before any code-split chunk can call a bare
// getFirestore() (2026-08-19): ~40 call sites across lazy chunks race this
// module's initializeFirestore — whichever runs first wins silently, so the
// entry point pins the order. Also routes the OAuth popup window onto the
// memory cache (never the multi-tab IndexedDB lease) — see utils/firestore.
import './utils/firestore';
import { setupAuthObserver } from './lib/auth';
// The new design's address (Miles, 2026-09-23): prest0.ai/prest0design/<route>
// is the same app with the router based at that prefix. Read once, before the
// first render — the basename is fixed for the life of the page.
import { appPathname, designPreviewBase, designShows, startDesignPreview } from './design/previewPath';
// The chat's new-design skin, fetched wherever the new design's chat shows (design-system §7a.3, §7a.5).
import { loadPavilionChatSkin } from './design/pavilionChatDoor';
import { preloadPavilionHome } from './design/pavilion/PavilionHomeDoor';
import { preloadLandingPage } from './components/LandingPageDoor';
import { consumeLinkLanding, installLinkLanding } from './lib/linkLanding';

const DESIGN_PREVIEW_BASENAME = designPreviewBase();

// MetaMask and other browser extensions can throw noisy errors that break the UI overlay.
// Suppress them for both development and production builds so local testing isn’t blocked.
const suppressThirdPartyErrors = true;

// Force HTTPS in production to satisfy browser and Stripe security requirements
try {
  const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (window.location.protocol === 'http:' && !isLocal) {
    const target = 'https://' + window.location.host + window.location.pathname + window.location.search + window.location.hash;
    window.location.replace(target);
  }
} catch {}

// Apply polyfills and safety measures as early as possible
applyPolyfills();

// Set up global error handlers for animation and DOM-related errors
setupGlobalErrorHandlers();
// The chat's health record (2026-09-28): count freezes and slow interactions
// from the start. Counts stay on the page; only a signed-in person's beacon
// (loaded by the chat engine) sends them.
startChatHealthObservers();

// Initialize centralized auth observer once
(async () => {
  try { 
    await setupAuthObserver(app);
    
    // Canonical UID mapping is server-enforced; no client-side mapping loaders
  } catch {}
})();

// Utility: decide whether to suppress noisy third‑party extension errors (e.g., MetaMask)
function shouldSuppressThirdPartyError(input: unknown): boolean {
  const msg = typeof input === 'string' ? input : (typeof (input as any)?.message === 'string' ? (input as any).message : '');
  const stackOrFile = typeof (input as any)?.filename === 'string' ? (input as any).filename : (typeof (input as any)?.stack === 'string' ? (input as any).stack : '');
  const text = `${msg}\n${stackOrFile}`.toLowerCase();
  // MetaMask extension (chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn) or generic chrome-extension errors
  if (text.includes('metamask') || text.includes('chrome-extension://')) return suppressThirdPartyErrors;
  return false;
}

// Add global error handler to catch and log animation-related errors
const originalError = console.error;
console.error = function(...args) {
  // Check if this is an animation-related error
  const isAnimationError = args.some(arg => {
    if (typeof arg !== 'string') return false;
    return (
      arg.includes("Cannot use 'in' operator to search for 'animation'") ||
      (arg.includes("Cannot read property") && arg.includes("animation"))
    );
  });

  // Check if this is a position-related error
  const isPositionError = args.some(arg => {
    if (typeof arg !== 'string') return false;
    return (arg.includes("Cannot set properties of undefined") && arg.includes("position"));
  });

  const isThirdParty = args.some(arg => shouldSuppressThirdPartyError(arg));

  // If it's an animation, position, or third‑party extension error, handle gracefully
  if (isAnimationError || isPositionError || isThirdParty) {
    console.warn('Error intercepted:', ...args);
    // Don't propagate to avoid React's error boundary
    return;
  }

  // Otherwise, use the original error handler
  originalError.apply(console, args);
};

// Add a global error event listener
window.addEventListener('error', (event) => {
  const rawMessage = event.error?.message || event.message || '';
  const isThirdParty = shouldSuppressThirdPartyError(event.error || event.filename || rawMessage);
  
  // Handle specific types of errors
  if (
    (typeof rawMessage === 'string' && rawMessage.includes("Cannot use 'in' operator to search for 'animation'")) ||
    (typeof rawMessage === 'string' && rawMessage.includes("Cannot set properties of undefined") && rawMessage.includes("position")) ||
    isThirdParty
  ) {
    // Prevent the error from bubbling up
    event.preventDefault();
    console.warn('Suppressed error:', rawMessage);
  }
}, true);

// Also suppress unhandled promise rejections from third‑party extensions
window.addEventListener('unhandledrejection', (event) => {
  const reason = (event as PromiseRejectionEvent).reason;
  if (shouldSuppressThirdPartyError(reason)) {
    event.preventDefault();
    console.warn('Suppressed unhandledrejection from extension:', reason?.message || reason);
  }
}, true);

// Every spinner turns on one clock (D-71): a spinner rebuilt by a repaint carries on where the last one was, instead of
// snapping back to its start every second, and every spinner on screen turns in step (utils/markSpinnerPhase.ts).
installMarkSpinnerPhase();

// Patch fetch to handle network errors gracefully without blocking init.
// IMPORTANT: API proxy calls (anthropicProxy, perplexityProxy) are re-thrown
// so the orchestrator's own retry logic works on real errors.
// A request the browser cancels because the page is being left is logged as a
// warning, never an error (fetchFailureLog.ts — 2026-09-24).
(() => {
  installPageLeavingWatch();
  const originalFetch = window.fetch.bind(window);
  const patchedFetch: typeof window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args as Parameters<typeof window.fetch>);
      return response as Response;
    } catch (error) {
      // Extract URL to decide whether to re-throw or mask
      const url = typeof args[0] === 'string'
        ? args[0]
        : args[0] instanceof Request ? args[0].url : '';

      // Re-throw for API proxy calls — orchestrator/perplexity have their own retry logic
      if (url.includes('cloudfunctions.net') || url.includes('anthropicProxy') || url.includes('perplexityProxy')) {
        logFetchFailure('api-proxy', url, error);
        throw error;
      }

      // Re-throw for Firebase AUTH endpoints too: masking these as a synthetic
      // 500 made the Auth SDK classify a transient network blip as a SERVER
      // error (auth/internal-error) instead of auth/network-request-failed —
      // so sign-in's own retry/messaging never engaged, and the first attempt
      // from a cold connection (fresh incognito profile) failed opaquely while
      // an immediate retry succeeded (2026-08-19 first-attempt sign-in failure).
      if (url.includes('identitytoolkit.googleapis.com') || url.includes('securetoken.googleapis.com')) {
        logFetchFailure('firebase-auth', url, error);
        throw error;
      }

      // Keep synthetic response for non-API calls (Firebase SDK, analytics, polling, etc.)
      console.warn('Fetch error:', error);
      return new Response(JSON.stringify({ error: 'Network error', message: String(error) }), {
        status: 500,
        headers: { 'Content-Type': 'application/json' }
      });
    }
  };
  window.fetch = patchedFetch;
})();

// Function to detect if we're running on Google Cloud Storage
const isRunningOnGCS = () => {
  return window.location.hostname === 'storage.googleapis.com' || 
         window.location.href.includes('storage.googleapis.com');
};

// Links land on the page (src/lib/linkLanding.ts; Miles, 2026-10-15): a tab a link opened starts with the chat
// closed at the bottom right (read before the chat's first frame), and from here every link to another page opens
// in a new tab, the person's own chat mode staying their default.
consumeLinkLanding();
installLinkLanding();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Wherever the new design's chat shows (the tree, and prest0.ai while
// config/site-design.json lists `chat` as live): <html data-design>, which the
// chat's skin is scoped by. Inside the tree only: the robots tag that keeps the
// demonstration out of search, and the link keeper for plain anchors.
startDesignPreview();
// …and the chat's skin, requested now so it is in the page before the chat
// engine lands after first paint. Where the new chat does not show, a no-op.
loadPavilionChatSkin();
// …and, on the home page, the chunk of whichever home page it shows (the new
// design's while config/site-design.json lists `home`, today's otherwise):
// requested before the first render, so the headline does not wait for the
// router to mount the door and ask then. Neither home page is on the first
// download itself.
if (appPathname() === '/') {
  if (designShows('home')) preloadPavilionHome();
  else preloadLandingPage();
}

// …and the shell's structured-data blocks that belong to one page, read before the head manager's first write removes
// them from every other page (src/config/shellStructuredData.ts): the page each describes writes it again from here.
captureShellStructuredData();

root.render(
  <React.StrictMode>
    <HelmetProvider>
      {/* FIRST, so every page's own head overrides the site's defaults (src/components/SiteDefaultHead.tsx) */}
      <SiteDefaultHead />
      <ErrorBoundary>
        <BrowserRouter basename={DESIGN_PREVIEW_BASENAME || undefined}>
          <AppProvider>
            <GlobalStyles />
            <App />
          </AppProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>
);

// Handshake with the inline splash watchdog in public/index.html.
//
// The watchdog fires a "couldn't finish loading" recovery overlay if #root
// is still empty after SPLASH_HARD_TIMEOUT_MS. On slow networks or
// mid-range mobile, parse/eval + first React commit of the main bundle
// can lose that race even when the app is mounting fine — leaving users
// staring at the failure overlay one beat before the real UI paints.
//
// We set this flag the instant root.render() returns (synchronously: React
// has at least scheduled the first commit). The watchdog reads the flag
// in its setTimeout callbacks and short-circuits the overlay if mount has
// started, while still firing for *genuinely* broken deploys (script 404,
// module-eval error, no React invocation at all).
try {
  (window as unknown as { __PRESTON_REACT_MOUNTED__?: boolean }).__PRESTON_REACT_MOUNTED__ = true;
} catch {}

// Real-user Core Web Vitals (2026-09-09). Started after render() so it never
// competes with first paint, and it lazy-imports the library itself. First-party
// endpoint, no identifiers, no storage — see src/reportWebVitals.ts.
try {
  const start = () => {
    void import('./reportWebVitals').then((m) => m.reportWebVitals()).catch(() => {});
  };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as unknown as {
      requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback(start, { timeout: 4000 });
  } else {
    setTimeout(start, 2000);
  }
} catch {}

// Fade out and remove the minimal HTML splash after the first app paint.
//
// If React commits successfully, this runs within one setTimeout tick and
// cleanly wins the race against the inline splash watchdog in
// public/index.html. If React fails to paint, the public/index.html
// watchdog (4 s hard timer + window 'error' hook on main.*.js) takes
// over and ensures the splash is still removed so users never see a
// stuck brand screen.
(() => {
  // FH-QA-2026-05-21 fix: the splash watchdog in public/index.html
  // appends a free-floating `#app-splash-still-loading` subtitle to
  // <body> at the 6 s soft-hint mark when #root is still empty. On
  // slow CPU / slow network where React commits at ~6.5 s, that
  // subtitle was being orphaned forever — leaving the gold
  // "still loading…" line floating on top of a fully rendered
  // LandingPage. Always tear down both the splash AND the still-
  // loading subtitle as soon as React mounts.
  const removeStillLoading = () => {
    try {
      const hint = document.getElementById('app-splash-still-loading');
      if (hint && hint.parentNode) hint.parentNode.removeChild(hint);
    } catch {}
  };
  const hideSplash = () => {
    try {
      const splash = document.getElementById('app-splash');
      let alreadyRemoved = false;
      const removeSplash = () => {
        if (alreadyRemoved) return;
        alreadyRemoved = true;
        if (splash && splash.parentNode) splash.parentNode.removeChild(splash);
        // Always clear the still-loading subtitle once React has
        // mounted, even if #app-splash itself was already gone
        // (e.g. watchdog removed it on a transient error path).
        removeStillLoading();
      };
      if (!splash) {
        // Splash already removed (or never existed) but the watchdog
        // may still have appended the subtitle independently. Clean
        // it up so the UI does not look stuck.
        removeStillLoading();
        return;
      }
      splash.addEventListener('transitionend', removeSplash, { once: true });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          splash.classList.add('hidden');
          // Fallback in case transitionend doesn't fire
          window.setTimeout(removeSplash, 500);
        });
      });
    } catch {}
  };
  // Defer to allow React to commit and paint at least once
  setTimeout(hideSplash, 0);
})();